/**
 * Wire global error-capture hooks to a {@link TelemetryClient}. Three sources
 * are installed here automatically at boot; the other two automatic global
 * handlers (React Error Boundary at the app root + the fetch interceptor in
 * `apiTelemetry`) are wired at their respective call sites.
 *
 *   1. `window.error`              - synchronous runtime errors.
 *   2. `window.unhandledrejection` - un-awaited promise rejections.
 *   3. `Office.onReady` wrapper    - uncaught errors thrown inside Office.js
 *                                    ready callbacks. Office.js invokes those
 *                                    callbacks itself and swallows a throw
 *                                    (it never reaches `window.onerror`), so
 *                                    without this wrapper an error in an Office
 *                                    callback is invisible. Registering it here
 *                                    makes it a genuine automatic handler - no
 *                                    manual `logOfficeError` call is required.
 *
 * The hooks call `enqueueError` with a pre-scrubbed message. The
 * scrubber strips PII before the event ever reaches the in-memory queue.
 *
 * Return value: a cleanup function. Tests use it; production callers
 * normally never tear the hooks down.
 */

import type { TelemetryClient } from "./client";
import { scrubMessage } from "./scrub";

export interface CaptureHookOptions {
  /** The window-like target. Default `globalThis.window`. Tests can pass jsdom. */
  target?: EventTarget & {
    addEventListener: (type: string, listener: EventListener) => void;
    removeEventListener: (type: string, listener: EventListener) => void;
  };
  /**
   * The Office namespace to hook. Defaults to the global `Office`. Tests pass a
   * synthetic object so the wrap can be verified without a real Office host.
   */
  office?: OfficeLike;
}

/** Minimal shape of the global `Office` object this module hooks. */
export interface OfficeLike {
  onReady?: (callback?: (info: unknown) => unknown) => unknown;
}

/** Install global error hooks. Returns a cleanup function. */
export function installCaptureHooks(
  client: TelemetryClient,
  options: CaptureHookOptions = {},
): () => void {
  // Default to `globalThis`. In a browser globalThis === window and inherits
  // every EventTarget method we need (Window extends EventTarget in lib.dom),
  // so `globalThis` is already structurally assignable to `EventTarget` -
  // no cast needed. Tests pass a synthetic EventTarget via `options.target`
  // so we never touch the real DOM.
  const target: EventTarget = options.target ?? globalThis;

  // 1. Synchronous errors
  const errorListener = (ev: Event): void => {
    const e = ev as ErrorEvent;
    client.enqueueError("error.unhandled", {
      type: nameOf(e.error) ?? "Error",
      scrubbedMessage: scrubMessage(e.message ?? messageOf(e.error)),
    });
  };
  target.addEventListener("error", errorListener);

  // 2. Unhandled promise rejections
  const rejectionListener = (ev: Event): void => {
    const r = ev as PromiseRejectionEvent;
    const reason: unknown = r.reason;
    client.enqueueError("error.promise-rejection", {
      type: nameOf(reason) ?? typeofLabel(reason),
      scrubbedMessage: scrubMessage(messageOf(reason) ?? String(reason)),
    });
  };
  target.addEventListener("unhandledrejection", rejectionListener);

  // 3. Office.js ready-callback wrapper (automatic - see module header).
  const removeOfficeHook = installOfficeErrorHook(client, options.office);

  return () => {
    target.removeEventListener("error", errorListener);
    target.removeEventListener("unhandledrejection", rejectionListener);
    removeOfficeHook();
  };
}

/** Marker so a re-init doesn't wrap an already-wrapped `Office.onReady`. */
const OFFICE_HOOK_MARKER = "__aivaTelemetryOfficeHook__";

/**
 * Monkey-patch `Office.onReady` so any error thrown (or promise rejected) by a
 * ready callback is captured as an automatic `error.office-js` event, then
 * re-thrown so existing behaviour is unchanged. Returns a cleanup that restores
 * the original `Office.onReady`.
 *
 * No-op (returns an empty cleanup) when there is no Office host - e.g. unit
 * tests or the auth dialog surface - so importing the telemetry module never
 * depends on Office being present.
 *
 * Exported for direct testing; `installCaptureHooks` calls it at boot.
 */
export function installOfficeErrorHook(
  client: TelemetryClient,
  office?: OfficeLike,
): () => void {
  const target: OfficeLike | undefined =
    office ?? (globalThis as { Office?: OfficeLike }).Office;
  if (!target || typeof target.onReady !== "function") {
    return () => {
      /* no Office host - nothing to hook */
    };
  }

  // Already wrapped by a previous init - leave it in place and hand back a
  // no-op so we never stack wrappers or lose the original reference.
  const current = target.onReady as ((cb?: (info: unknown) => unknown) => unknown) & {
    [OFFICE_HOOK_MARKER]?: boolean;
  };
  if (current[OFFICE_HOOK_MARKER]) {
    return () => {
      /* another install owns the wrap */
    };
  }

  // Keep the ORIGINAL reference (not a bound copy) so cleanup restores exact
  // identity. Invoke it via `.call(target)` to preserve the `this` binding.
  const original = current;

  const emit = (err: unknown): void => {
    client.enqueueError("error.office-js", {
      type: nameOf(err) ?? "OfficeJsError",
      scrubbedMessage: scrubMessage(messageOf(err) ?? String(err)),
    });
  };

  const wrapped = ((callback?: (info: unknown) => unknown) => {
    if (typeof callback !== "function") {
      return original.call(target, callback);
    }
    const safeCallback = (info: unknown): unknown => {
      try {
        const result = callback(info);
        // Office.onReady callbacks may be async - capture a rejection too,
        // then re-reject so Office's own promise chain is unaffected.
        if (result && typeof (result as PromiseLike<unknown>).then === "function") {
          return (result as Promise<unknown>).then(undefined, (err: unknown) => {
            emit(err);
            throw err;
          });
        }
        return result;
      } catch (err) {
        emit(err);
        throw err;
      }
    };
    return original.call(target, safeCallback);
  }) as typeof current;
  wrapped[OFFICE_HOOK_MARKER] = true;

  target.onReady = wrapped;

  return () => {
    // Only restore if we're still the installed wrapper.
    if (target.onReady === wrapped) {
      target.onReady = original;
    }
  };
}

/** Best-effort extraction of a constructor name from `Error`-like values. */
function nameOf(value: unknown): string | undefined {
  if (value instanceof Error) return value.name;
  if (value && typeof value === "object" && "name" in value) {
    const n = (value as { name?: unknown }).name;
    return typeof n === "string" ? n : undefined;
  }
  return undefined;
}

function messageOf(value: unknown): string | undefined {
  if (value instanceof Error) return value.message;
  if (value && typeof value === "object" && "message" in value) {
    const m = (value as { message?: unknown }).message;
    return typeof m === "string" ? m : undefined;
  }
  return undefined;
}

function typeofLabel(value: unknown): string {
  if (value === null) return "null";
  return typeof value;
}
